import { t } from "@lingui/macro";
import { format, parse, parseISO } from "date-fns";

import { RecurrenceFormValue } from "@/features/RecurringRequests/types";

export const defaultRecurrenceValue = (): RecurrenceFormValue => ({
  recurrenceType: "WEEK",
  repeatInterval: 1,
  endCondition: "AFTER_OCCURRENCES",
  occurrenceCount: 4,
});

/**
 * Pattern summary for the recurrence review row, e.g. "Every Week on Tuesday".
 */
export const recurrencePatternLabel = ({
  recurrenceType,
  repeatInterval,
  sourceDate,
}: {
  recurrenceType: RecurrenceFormValue["recurrenceType"] | null | undefined;
  repeatInterval: number | null | undefined;
  sourceDate?: string | null;
}) => {
  if (recurrenceType === "DAY") return t`Every Day`;

  if (recurrenceType === "MONTH") {
    if (!sourceDate) return t`Every Month`;
    return t`Every Month on the ${format(parseISO(sourceDate), "do")}`;
  }

  if (recurrenceType === "WEEK" && repeatInterval === 2) {
    if (!sourceDate) return t`Every Other Week`;
    return t`Every Other Week on ${format(parseISO(sourceDate), "EEEE")}`;
  }

  if (!sourceDate) return t`Every Week`;
  return t`Every Week on ${format(parseISO(sourceDate), "EEEE")}`;
};

/**
 * Compact cadence word for the dates-modal subtitle, e.g. "Daily" instead of
 * the source weekday, which can look like a weekly Tuesday series.
 */
export const recurrenceCadenceLabel = ({
  recurrenceType,
  repeatInterval,
}: {
  recurrenceType?: RecurrenceFormValue["recurrenceType"] | null;
  repeatInterval?: number | null;
}) => {
  if (recurrenceType === "DAY") return t`Daily`;
  if (recurrenceType === "MONTH") return t`Monthly`;
  if (recurrenceType === "WEEK" && repeatInterval === 2) return t`Biweekly`;
  if (recurrenceType === "WEEK") return t`Weekly`;
  return null;
};

export const recurringRequestDatesHeaderCadenceAndTime = ({
  recurrenceType,
  repeatInterval,
  timeLabel,
}: {
  recurrenceType?: RecurrenceFormValue["recurrenceType"] | null;
  repeatInterval?: number | null;
  timeLabel?: string | null;
}) =>
  [recurrenceCadenceLabel({ recurrenceType, repeatInterval }), timeLabel]
    .filter(Boolean)
    .join(", ");

export const formatSeriesEndDate = (date?: string | null) =>
  date ? format(parseISO(date), "MMM do, yyyy") : null;

export const lastRequestedSeriesDate = (
  dates: Array<string | null | undefined>
) =>
  dates
    .filter((date): date is string => !!date)
    .sort()
    .at(-1) ?? null;

export const formatTime = (value?: string | null) =>
  value ? format(parse(value, "HH:mm:ss", new Date()), "h:mm a") : null;

export const formatTimeRange = (
  startAt?: string | null,
  endAt?: string | null
) => {
  const start = formatTime(startAt);
  const end = formatTime(endAt);

  return start && end ? `${start} - ${end}` : null;
};

export const formatStatusTime = (
  startAt?: string | null,
  endAt?: string | null
) => {
  const start = formatTime(startAt);
  const end = formatTime(endAt);

  if (start && end) return `${start} - ${end}`;
  return start ?? end ?? null;
};
